/**
 * Desktop dropdown under the header "Catalog" link (snippets/catalog-dropdown.liquid).
 * Opens on hover (with hover intent) and on keyboard focus; the Catalog link itself still navigates.
 * Closes on mouse leave, Escape, a click outside and when focus leaves the link + panel.
 * The panel is a manual popover (top layer), placed under the link with fixed coordinates, so the
 * header's overflow can't clip it and the header layout doesn't shift.
 */
const OPEN_DELAY = 120;
const CLOSE_DELAY = 200;
const GAP = 10;

/** @param {HTMLAnchorElement} trigger */
function setup(trigger) {
  const panel = /** @type {HTMLElement | null} */ (document.getElementById(trigger.getAttribute('aria-controls') || ''));
  if (!panel || trigger.dataset.catalogReady) return;
  trigger.dataset.catalogReady = 'true';
  const supportsPopover = typeof panel.showPopover === 'function';
  /** @type {number | undefined} */
  let timer;
  let open = false;
  // Set while focus is moved back to the link on Escape, so that focus doesn't reopen the panel
  let restoringFocus = false;

  const place = () => {
    const rect = trigger.getBoundingClientRect();
    const width = panel.offsetWidth;
    const left = Math.max(16, Math.min(rect.left - 16, window.innerWidth - width - 16));
    panel.style.setProperty('--catalog-dropdown-gap', `${GAP}px`);
    panel.style.top = `${Math.round(rect.bottom + GAP)}px`;
    panel.style.left = `${Math.round(left)}px`;
  };

  const show = () => {
    clearTimeout(timer);
    if (open) return;
    // Only where the desktop menu shows the link inline (not inside the "More" overflow list)
    const item = trigger.closest('li');
    if (item && item.getAttribute('slot') === 'overflow') return;
    if (trigger.getBoundingClientRect().width === 0) return;
    open = true;
    panel.classList.add('is-open');
    if (supportsPopover && !panel.matches(':popover-open')) panel.showPopover();
    place();
    trigger.setAttribute('aria-expanded', 'true');
    window.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', hide);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
  };

  const hide = () => {
    clearTimeout(timer);
    if (!open) return;
    open = false;
    panel.classList.remove('is-open');
    if (supportsPopover && panel.matches(':popover-open')) panel.hidePopover();
    trigger.setAttribute('aria-expanded', 'false');
    window.removeEventListener('scroll', place);
    window.removeEventListener('resize', hide);
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('keydown', onKeyDown);
  };

  const later = (/** @type {() => void} */ fn, /** @type {number} */ delay) => {
    clearTimeout(timer);
    timer = window.setTimeout(fn, delay);
  };

  const inside = (/** @type {EventTarget | null} */ node) =>
    node instanceof Node && (trigger.contains(node) || panel.contains(node));

  /** @param {PointerEvent} event */
  function onPointerDown(event) {
    if (!inside(event.target)) hide();
  }

  /** @param {KeyboardEvent} event */
  function onKeyDown(event) {
    if (event.key !== 'Escape') return;
    const focusWasInside = inside(document.activeElement);
    hide();
    if (focusWasInside) {
      restoringFocus = true;
      trigger.focus();
      restoringFocus = false;
    }
  }

  for (const el of [trigger, panel]) {
    el.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      if (open) clearTimeout(timer);
      else later(show, OPEN_DELAY);
    });
    // Chromium can fire a stray leave inside the panel (the menu list is in a shadow root), so only a move
    // to somewhere outside the link and panel counts, and any move over them cancels a pending close
    el.addEventListener('pointerover', (event) => {
      if (event.pointerType === 'mouse' && open) clearTimeout(timer);
    });
    el.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse' || inside(event.relatedTarget)) return;
      if (open) later(hide, CLOSE_DELAY);
      else clearTimeout(timer);
    });
    el.addEventListener('focusout', (event) => {
      if (!inside(event.relatedTarget)) later(hide, 0);
    });
  }

  trigger.addEventListener('focusin', () => {
    if (!restoringFocus && trigger.matches(':focus-visible')) show();
  });
  // A click on the link navigates; don't leave the panel flashing open while the page changes
  trigger.addEventListener('click', () => clearTimeout(timer));
}

function init() {
  document.querySelectorAll('a[data-catalog-trigger]').forEach((el) => setup(/** @type {HTMLAnchorElement} */ (el)));
}

init();
// The header is re-rendered when edited in the theme editor
document.addEventListener('shopify:section:load', init);
